/**
 * <s-campaign-video> — Background campaign video with an always-available pause control (WCAG 2.2.2).
 * The optional toggle button targets this element with commandfor/command="--toggle".
 * Without a toggle, the video still respects prefers-reduced-motion (stays paused). Starts paused under prefers-reduced-motion; an explicit pause stays until the visitor presses play.
 */
class SCampaignVideo extends HTMLElement {
  #controller;
  #video;
  #toggle;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#video = this.querySelector('video');
    this.#toggle = this.querySelector('[data-toggle]');
    if (!this.#video) return;

    this.#video.muted = true;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#video.removeAttribute('autoplay');
      this.#video.pause();
    }

    this.addEventListener('command', (event) => {
      if (event.command !== '--toggle') return;
      if (this.#video.paused) {
        this.#video.play().catch(() => {});
      } else {
        this.#video.pause();
      }
    }, { signal });
    this.#video.addEventListener('play', () => this.#sync(), { signal });
    this.#video.addEventListener('pause', () => this.#sync(), { signal });
    this.#sync();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  #sync() {
    const paused = this.#video.paused;
    const toggle = this.#toggle;
    if (!toggle) return;
    toggle.setAttribute('aria-pressed', String(paused));
    toggle.setAttribute('aria-label', paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause);
    toggle.querySelector('[data-icon-pause]')?.toggleAttribute('hidden', paused);
    toggle.querySelector('[data-icon-play]')?.toggleAttribute('hidden', !paused);
  }
}

if (!customElements.get('s-campaign-video')) {
  customElements.define('s-campaign-video', SCampaignVideo);
}
